import { Col, Form, Grid, Input, Row, Tag, Tooltip, Button } from "antd";
import dayjs from "dayjs";
import { useRef, useState } from "react";
import isEqual from "react-fast-compare";
import Icon from 'src/components/Icon/Icon';
import {
  Amount,
  ColumnType,
  CustomerStatistics,
  CustomerStatisticsHandler,
  DatePicker,
  PageContainer,
  PageHeader,
  SearchButton,
  Select,
  Table,
} from "src/components";
import { useCustomerGameStats, useCustomerLogs, usePrepareGameSessions } from "src/hooks";
import { CustomerLogType } from "src/types";
import { datetime, formatDatetime } from "src/utils";
import BonusColumn from "src/components/TableColumns/BonusTypeColumn";
import ChannelLogo, { GameChannel } from "src/components/TableColumns/ChannelLogo";
import CopyableRef from "src/components/TableColumns/CopyableRef";
import SportGameName from "src/components/TableColumns/SportGameName";
import {
  AmountColor,
  LEGACY_GAME_PROVIDER,
  getRowGameProvider,
  getGameProviderName,
  getGameChannel,
  ALL_CHANNELS,
  isSportBill,
  getProvidersOfChannel,
  getGameProvidersFilter,
} from "./helpers";
import GameSessionTab from "./GameSessionTab";
import "./SearchWalletTransaction.less";

enum CustomerLogGroup {
  ALL = "ALL",
  CREDIT = "CREDIT",
  DEBIT = "DEBIT",
  GAME = "GAME",
  BONUS = "BONUS",
  VOID = "VOID",
  TOURNAMENT = "TOURNAMENT",
  TIP_CREDIT = "TIP_CREDIT",
  TIP_DEBIT = "TIP_DEBIT",
  RAIN_CREDIT = "RAIN_CREDIT",
  RAIN_DEBIT = "RAIN_DEBIT",
  TICKET = "TICKET",
  CONVERT = "CONVERT",
  CREDITANDDEBIT = "CREDITANDDEBIT",
  FREE_SPIN = "FREE_SPIN",
  PG_FREE_SPIN = "PG_FREE_SPIN",
  CASINO_COMMISSION_01_PERCENT = "CASINO_COMMISSION_01_PERCENT",
  CASINO_COMMISSION_LOSING_STREAK = "CASINO_COMMISSION_LOSING_STREAK",
  PIGHUNT = "PIGHUNT",
  CASINO_TIPS = "CASINO_TIPS"
}

function SearchCustomerLog() {
  const [form] = Form.useForm();
  // Phones (below antd md), per the BO mobile design: filters stacked (channel | provider side by side), the
  // search button last at full width, tighter spacing, and no frozen table columns (the four frozen ones alone
  // are wider than the screen). Desktop keeps the original layout.
  const isSmallScreen = Grid.useBreakpoint().md === false;
  const fieldProps = isSmallScreen ? { style: { marginBottom: 8 }, labelCol: { style: { paddingBottom: 4 } } } : {};
  // Select's own `width` prop wins over style.width, so full width has to go through the prop.
  const selectWidth = isSmallScreen ? "100%" : undefined;
  const [currentTab, setCurrentTab] = useState(0);
  const [filters, setFilters] = useState<{
    skip: number;
    limit: number;
    types: CustomerLogType[];
    startDate: string;
    endDate: string;
    customerCode: string;
    gameProviders?: string[];
  }>();
  const currentGroup = form.getFieldValue("group");
  const customerStatistics = useRef<CustomerStatisticsHandler>(null);
  const {
    data: customerLogs,
    isFetching: isFetchingCustomerLogs,
    refetch,
  } = useCustomerLogs(
    filters ? filters.customerCode : "",
    {
      skip: filters?.skip,
      limit: filters?.limit,
      startDate: filters?.startDate,
      endDate: filters?.endDate,
      types: filters?.types,
      gameProviders: filters?.gameProviders,
    },
    {
      enabled: Boolean(filters) && currentTab === 0,
      keepPreviousData: true,
    }
  );

  const { data: gameStats, isFetching: isFetchingGameStats } = useCustomerGameStats(
    filters ? filters.customerCode : "",
    {
      startDate: filters?.startDate,
      endDate: filters?.endDate,
    },
    {
      enabled: Boolean(filters) && currentTab === 1,
      keepPreviousData: true,
    }
  );

  const { mutate: prepareGameSessions } = usePrepareGameSessions();

  const onFinish = async (values: any) => {
    // Head start for the game-session tab while support is still on the wallet tab.
    prepareGameSessions(values.customerCode);
    const newFilters = {
      skip: 0,
      limit: 100,
      customerCode: values.customerCode,
      startDate: formatDatetime(values.datetimeRange[0]),
      endDate: formatDatetime(values.datetimeRange[1]),
      gameProviders: getGameProvidersFilter(values.channel, values.gameProvider),
      types: {
        [CustomerLogGroup.CREDIT]: [CustomerLogType.CREDIT],
        [CustomerLogGroup.DEBIT]: [CustomerLogType.DEBIT],
        [CustomerLogGroup.GAME]: [CustomerLogType.GAME],
        [CustomerLogGroup.BONUS]: [CustomerLogType.BONUS],
        [CustomerLogGroup.VOID]: [CustomerLogType.VOID],
        [CustomerLogGroup.CONVERT]: [CustomerLogType.CONVERT],
        [CustomerLogGroup.TOURNAMENT]: [CustomerLogType.TOURNAMENT],
        [CustomerLogGroup.TIP_CREDIT]: [CustomerLogType.TIP_CREDIT],
        [CustomerLogGroup.TIP_DEBIT]: [CustomerLogType.TIP_DEBIT],
        [CustomerLogGroup.RAIN_CREDIT]: [CustomerLogType.RAIN_CREDIT],
        [CustomerLogGroup.RAIN_DEBIT]: [CustomerLogType.RAIN_DEBIT],
        [CustomerLogGroup.PIGHUNT]: [CustomerLogType.PIGHUNT],
        [CustomerLogGroup.TICKET]: [CustomerLogType.TICKET],
        [CustomerLogGroup.FREE_SPIN]: [CustomerLogType.FREE_SPIN],
        [CustomerLogGroup.PG_FREE_SPIN]: [CustomerLogType.PG_FREE_SPIN],
        [CustomerLogGroup.CREDITANDDEBIT]: [CustomerLogType.CREDIT, CustomerLogType.DEBIT],
        [CustomerLogGroup.CASINO_COMMISSION_01_PERCENT] : [CustomerLogType.CASINO_COMMISSION_01_PERCENT],
        [CustomerLogGroup.CASINO_COMMISSION_LOSING_STREAK] : [CustomerLogType.CASINO_COMMISSION_LOSING_STREAK],
        [CustomerLogGroup.CASINO_TIPS] : [CustomerLogType.CASINO_TIPS],
        [CustomerLogGroup.ALL]: [
          CustomerLogType.CREDIT,
          CustomerLogType.DEBIT,
          CustomerLogType.GAME,
          CustomerLogType.BONUS,
          CustomerLogType.VOID,
          CustomerLogType.CONVERT,
          CustomerLogType.TOURNAMENT,
          CustomerLogType.TIP_CREDIT,
          CustomerLogType.TIP_DEBIT,
          CustomerLogType.RAIN_CREDIT,
          CustomerLogType.RAIN_DEBIT,
          CustomerLogType.TICKET,
          CustomerLogType.FREE_SPIN,
          CustomerLogType.PG_FREE_SPIN,
          CustomerLogType.CASINO_COMMISSION_01_PERCENT,
          CustomerLogType.CASINO_COMMISSION_LOSING_STREAK,
          CustomerLogType.PIGHUNT,
          CustomerLogType.CASINO_TIPS,
        ],
      }[values.group as CustomerLogGroup],
    };

    if (isEqual(filters, newFilters)) {
      refetch();
      customerStatistics.current?.refetchBalance();
    } else {
      setFilters(newFilters);
    }
    // The game-session tab reads the same filters, so a new search stays on it.
    if (currentTab !== 2) setCurrentTab(0);
  };

  const bonusColumnExcluded =
    currentGroup === CustomerLogGroup.DEBIT || currentGroup === CustomerLogGroup.GAME || currentGroup === CustomerLogGroup.VOID;
  const payoutColumnExcluded =
    currentGroup === CustomerLogGroup.CREDIT || currentGroup === CustomerLogGroup.DEBIT;

  const columns: ColumnType<any>[][] = [
    [
      {
        title: "วันที่",
        dataIndex: "createdAt",
        key: 'date',
        width: 105,
        fixed: isSmallScreen ? undefined : 'left',
        render: (value, record) => {
          const date = record.betAt ?? value;
          return dayjs(date).isValid() ? datetime(date).format("DD/MM/YYYY") : '';
        },
      },
      {
        title: "เวลาแทง",
        dataIndex: "createdAt",
        key: 'betTime',
        width: 90,
        fixed: isSmallScreen ? undefined : 'left',
        render: (value, record) => {
          const betTime = record.betAt ?? value;
          return dayjs(betTime).isValid() ? datetime(betTime).format("HH:mm:ss") : '';
        },
      },
      {
        title: "เวลาผลออก",
        dataIndex: "settledAt",
        key: 'settleTime',
        width: 95,
        fixed: isSmallScreen ? undefined : 'left',
        render: (value, record) => {
          if (value && dayjs(value).isValid()) return datetime(value).format("HH:mm:ss");
          if (isSportBill(record)) return <Tag color="orange">รอผล</Tag>;
          return '—';
        },
      },
      {
        title: "รายการ",
        dataIndex: "transactionType",
        width: 170,
        fixed: isSmallScreen ? undefined : 'left',
        render: (value, record) => {
          switch (value) {
            case "Debit":
              return "ถอนเงิน";
            case "Credit":
              return "เติมเงิน";
            case "Game":
              if (!isSportBill(record)) return "เดิมพันเกม";
              return (
                <>
                  เดิมพันเกม{" "}
                  {record.payoutTransactionRefId
                    ? <Tag color="purple">ผลออก</Tag>
                    : <Tag color="orange">แทง</Tag>}
                </>
              );
            case "Bonus":
              if (record.bonusType === 'CASHBACK') return 'คืนยอดเสีย'
              return "ฟรีโบนัส";
            case "Void":
              return "ยกเลิกรายการ";
            case "CONVERT":
              return "แปลงเป็นเงิน";
            case "TICKET":
              return "แลก Ticket";
            case "TOURNAMENT":
              return "รางวัลผู้ชนะทัวร์นาเม้นท์";
            case "TIP_CREDIT":
              return "รับทิป";
            case "TIP_DEBIT":
              return "ส่งทิป";
            case "RAIN_CREDIT":
              return "รับเงินโปรย";
            case "RAIN_DEBIT":
              return "การโปรยเงิน";
            case "FREE_SPIN":
              return "แลกสปินรายวัน";
            case "PG_FREE_SPIN":
              return "แลกฟรีสปินเกม";
            case "CASINO_COMMISSION_01_PERCENT":
              return "โปรค่าคอมบาคาร่า 0.1% ทุกอาทิตย์";
            case "CASINO_COMMISSION_LOSING_STREAK":
              return "โปรค่าแทงเสีย 12 ไม้ บาคาร่า";
            case "PIGHUNT":
              return "Pighunt";
            case "CASINO_TIPS":
              return "ส่งทิป (ในเกม)";
          }
        },
      },
      {
        title: <div style={{ textAlign: 'center' }}>
          <Tooltip placement="top" title="รับโบนัส">
            <Button type="text" style={{ padding: 0 }}>
              <Icon name="outline-tag" size="1rem" />
            </Button>
          </Tooltip>
        </div>,
        dataIndex: "isBonus",
        width: 50,
        align: "center",
        render: (value, record) => {
          if (record.transactionType === "Credit" || record.bonusType === 'CASHBACK') {
            return <BonusColumn isBonus={record.isBonus} bonusType={record.bonusType} />
          } else {
            return "-"
          }
        },
      },
      {
        title: "จำนวนเงิน",
        dataIndex: "amount",
        render: (value) => <AmountColor value={value} />,
      },
      {
        title: "โบนัส",
        dataIndex: "coinBonusAmount",
        width: 100,
        exclude: bonusColumnExcluded,
        render: (value) => <AmountColor value={value} />,
      },
      {
        title: "เทิร์นโอเวอร์ส่วนโบนัส",
        dataIndex: "turnOverAmount",
        width: 200,
        render: (value) => <AmountColor value={value} />,
      },
      {
        title: "ชนะเดิมพัน",
        dataIndex: "payoutAmount",
        exclude: payoutColumnExcluded,
        render: (value) => <AmountColor value={value} />,
      },
      {
        title: "ยอดเงินก่อนทำรายการ",
        dataIndex: "beforeBalance",
        render: (value) => <Amount value={value} />,
      },
      {
        title: "ยอดเงินหลังทำรายการ",
        dataIndex: "afterBalance",
        render: (value) => <Amount value={value} />,
      },
      {
        title: "ชื่อเกม",
        dataIndex: "gameName",
        render: (value, record) => <SportGameName gameName={value} detail={record.sportDetail} />,
      },
      {
        title: "ช่องทาง",
        dataIndex: "gameProvider",
        key: "channel",
        width: 90,
        render: (value, record) => <ChannelLogo channel={getGameChannel(getRowGameProvider(record))} />,
      },
      {
        title: "ค่ายเกม",
        dataIndex: "gameProvider",
        key: "gameProvider",
        render: (value, record) => getGameProviderName(getRowGameProvider(record)),
      },
      {
        title: "รหัสอ้างอิง",
        dataIndex: "parentBetId",
        width: 230,
        render: (value) => <CopyableRef value={value} />,
      },
    ],
    [
      {
        title: "ชื่อเกม",
        dataIndex: "gameName",
      },
      {
        title: "ค่ายเกม",
        dataIndex: "gameProvider",
        render: (value, record) => getGameProviderName(record?.gameProvider || LEGACY_GAME_PROVIDER),
      },
      {
        title: "จำนวนครั้งที่เล่น",
        dataIndex: "count",
        align: "right",
      },

    ],
  ];

  return (
    <PageContainer
      tabList={
        customerLogs
          ? [
            { tab: "รายการกระเป๋าเงิน", key: "0" },
            { tab: "รายการเกม", key: "1" },
            { tab: "รอบการเล่นรายเกม", key: "2" },
          ]
          : undefined
      }
      tabActiveKey={currentTab.toString()}
      onTabChange={(activeKey) => {
        setCurrentTab(parseInt(activeKey));
      }}
      header={
        <>
          <Form
            form={form}
            onFinish={onFinish}
            onValuesChange={(changed, values) => {
              // Keep the provider filter inside the chosen channel.
              if ("channel" in changed && values.gameProvider
                && !getProvidersOfChannel(changed.channel).includes(values.gameProvider)) {
                form.setFieldsValue({ gameProvider: undefined });
              }
            }}
            layout="vertical"
            initialValues={{
              group: CustomerLogGroup.ALL,
              channel: ALL_CHANNELS,
              datetimeRange: [
                dayjs().hour(0).minute(0).second(0).millisecond(0),
                dayjs().hour(23).minute(59).second(59).millisecond(999),
              ],
            }}
          >
            {/* Wraps on narrow screens (phones); with wrap off, the only flexible field (customer code) was
                squeezed to zero width once the channel / provider filters were added. */}
            <Row gutter={8}>
              <Col flex={isSmallScreen ? undefined : "1 1 220px"} span={isSmallScreen ? 24 : undefined}
                   order={isSmallScreen ? 1 : undefined}>
                <Form.Item name="customerCode" label="รหัสหรือเบอร์โทร" {...fieldProps}>
                  <Input size="large" placeholder="ใส่รหัสหรือเบอร์โทร" allowClear />
                </Form.Item>
              </Col>
              <Col span={isSmallScreen ? 24 : undefined} order={isSmallScreen ? 2 : undefined}>
                <Form.Item name="group" label="การทำรายการ" {...fieldProps}>
                  <Select width={selectWidth}>
                    <Select.Option value={CustomerLogGroup.ALL}>ทุกรายการ</Select.Option>
                    <Select.Option value={CustomerLogGroup.CREDIT}>เติมเงิน</Select.Option>
                    <Select.Option value={CustomerLogGroup.DEBIT}>ถอนเงิน</Select.Option>
                    <Select.Option value={CustomerLogGroup.CREDITANDDEBIT}>เติมเงินและถอนเงิน</Select.Option>
                    <Select.Option value={CustomerLogGroup.GAME}>เดิมพันเกม</Select.Option>
                    <Select.Option value={CustomerLogGroup.BONUS}>โบนัส</Select.Option>
                    <Select.Option value={CustomerLogGroup.CONVERT}>แปลงเป็นเงิน</Select.Option>
                    <Select.Option value={CustomerLogGroup.TOURNAMENT}>รางวัลผู้ชนะทัวร์นาเม้นท์</Select.Option>
                    <Select.Option value={CustomerLogGroup.TIP_CREDIT}>รับทิป</Select.Option>
                    <Select.Option value={CustomerLogGroup.TIP_DEBIT}>ส่งทิป</Select.Option>
                    <Select.Option value={CustomerLogGroup.RAIN_CREDIT}>รับโปรยเงิน</Select.Option>
                    <Select.Option value={CustomerLogGroup.RAIN_DEBIT}>การโปรยเงิน</Select.Option>
                    <Select.Option value={CustomerLogGroup.TICKET}>แลก Ticket</Select.Option>
                    <Select.Option value={CustomerLogGroup.VOID}>ยกเลิกรายการ</Select.Option>
                    <Select.Option value={CustomerLogGroup.FREE_SPIN}>แลกสปินรายวัน</Select.Option>
                    <Select.Option value={CustomerLogGroup.PG_FREE_SPIN}>แลกฟรีสปินเกม</Select.Option>
                    <Select.Option value={CustomerLogGroup.CASINO_COMMISSION_01_PERCENT}>โปรค่าคอมบาคาร่า 0.1% ทุกอาทิตย์</Select.Option>
                    <Select.Option value={CustomerLogGroup.CASINO_COMMISSION_LOSING_STREAK}>โปรค่าแทงเสีย 12 ไม้ บาคาร่า</Select.Option>
                    <Select.Option value={CustomerLogGroup.PIGHUNT}>Pighunt</Select.Option>
                    <Select.Option value={CustomerLogGroup.CASINO_TIPS}>ส่งทิป (ในเกม)</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={isSmallScreen ? 12 : undefined} order={isSmallScreen ? 3 : undefined}>
                <Form.Item name="channel" label="ช่องทาง" {...fieldProps}>
                  <Select dropdownWidth="220px" width={selectWidth}>
                    <Select.Option value={ALL_CHANNELS}>ทั้งหมด (SLOT · BET · SPORT)</Select.Option>
                    {(["SLOT", "BET", "SPORT"] as GameChannel[]).map((channel) => (
                      <Select.Option key={channel} value={channel}>
                        <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
                          <ChannelLogo channel={channel} />
                        </div>
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={isSmallScreen ? 12 : undefined} order={isSmallScreen ? 4 : undefined}>
                <Form.Item noStyle shouldUpdate={(prev, cur) => prev.channel !== cur.channel}>
                  {() => (
                    <Form.Item name="gameProvider" label="ค่ายเกม" {...fieldProps}>
                      <Select
                        dropdownWidth="220px"
                        width={selectWidth}
                        style={{ minWidth: isSmallScreen ? 0 : 160 }}
                        placeholder="ทุกค่าย"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                      >
                        {getProvidersOfChannel(form.getFieldValue("channel"))
                          .sort((a, b) => getGameProviderName(a).localeCompare(getGameProviderName(b)))
                          .map((provider) => (
                            <Select.Option key={provider} value={provider}>
                              {getGameProviderName(provider)}
                            </Select.Option>
                          ))}
                      </Select>
                    </Form.Item>
                  )}
                </Form.Item>
              </Col>
              <Col
                flex={isSmallScreen ? undefined : "0 1 340px"}
                span={isSmallScreen ? 24 : undefined}
                order={isSmallScreen ? 6 : undefined}
                style={{ minWidth: 0 }}
              >
                <Form.Item
                  name="datetimeRange"
                  className="wallet-date-range"
                  {...fieldProps}
                  label={
                    <Row wrap={false} style={{ width: "100%", whiteSpace: "nowrap" }}>
                      <Col span={12}>{"วันเวลาเริ่มต้น"}</Col>
                      <Col span={12}>{"วันเวลาสิ้นสุด"}</Col>
                    </Row>
                  }
                >
                  <DatePicker.RangePicker
                    size="large"
                    allowClear={false}
                    format="DD/MM/YYYY HH:mm"
                    showTime={{ format: "HH:mm" }}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col span={isSmallScreen ? 24 : undefined} order={isSmallScreen ? 7 : undefined}>
                <Form.Item
                  label={isSmallScreen ? undefined : <></>}
                  style={isSmallScreen ? { marginBottom: 16 } : undefined}
                  shouldUpdate={(prevValue, curValue) =>
                    prevValue.customerCode !== curValue.customerCode
                  }
                >
                  {() => (
                    <SearchButton
                      size="large"
                      block={isSmallScreen}
                      htmlType="submit"
                      disabled={!form.getFieldValue("customerCode")}
                    />
                  )}
                </Form.Item>
              </Col>
            </Row>
          </Form>
          {filters && <CustomerStatistics customerCode={filters.customerCode} ref={customerStatistics} />}
        </>
      }
    >
      {currentTab === 0 ? (
        <Table
          rowKey="uid"
          columns={columns[0]}
          dataSource={customerLogs ? customerLogs.results : []}
          loading={{
            size: "large",
            spinning: isFetchingCustomerLogs,
            style: { maxHeight: "calc(100vh - 310px)" },
          }}
          pagination={{
            showQuickJumper: !isSmallScreen,
            total: customerLogs?.total,
            current: filters ? filters.skip / filters.limit + 1 : 1,
            pageSize: filters ? filters.limit : 10,
            onChange: (page, pageSize) => {
              if (filters) {
                setFilters({ ...filters, skip: (page - 1) * pageSize, limit: pageSize });
              }
            },
          }}
          summary={(data) => {
            if (currentGroup !== CustomerLogGroup.ALL) {
              let totalAmount = 0;
              let totalCoinBonusAmount = 0;
              let totalPayoutAmount = 0;

              data.forEach(({ amount, coinBonusAmount, payoutAmount }) => {
                totalAmount += amount;
                totalCoinBonusAmount += coinBonusAmount;
                totalPayoutAmount += payoutAmount;
              });

              return (
                <Table.Summary fixed="bottom">
                  <Table.Summary.Row style={{ fontWeight: "bold" }}>
                    <Table.Summary.Cell index={0} colSpan={4}>
                      รวมทั้งหมด
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} />
                    <Table.Summary.Cell index={5}>
                      <AmountColor value={totalAmount} />
                    </Table.Summary.Cell>
                    {!bonusColumnExcluded && (
                      <Table.Summary.Cell index={6}>
                        {currentGroup === CustomerLogGroup.CREDIT && <AmountColor value={totalCoinBonusAmount} />}
                      </Table.Summary.Cell>
                    )}
                    <Table.Summary.Cell index={7} />
                    {!payoutColumnExcluded && (
                      <Table.Summary.Cell index={8}>
                        {currentGroup === CustomerLogGroup.GAME && <AmountColor value={totalPayoutAmount} />}
                      </Table.Summary.Cell>
                    )}
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }
          }}
        />
      ) : currentTab === 2 && filters ? (
        <GameSessionTab
          // Remount on a new search so paging starts from the first page.
          key={JSON.stringify([filters.customerCode, filters.startDate, filters.endDate, filters.gameProviders])}
          customerCode={filters.customerCode}
          startDate={filters.startDate}
          endDate={filters.endDate}
          gameProviders={filters.gameProviders}
        />
      ) : (
        <Table
          rowKey="uid"
          columns={columns[1]}
          dataSource={gameStats ? gameStats : []}
          loading={{
            size: "large",
            spinning: isFetchingGameStats,
            style: { maxHeight: "calc(100vh - 310px)" },
          }}
          pagination={{
            showQuickJumper: !isSmallScreen,
            total: gameStats?.length,
          }}
          summary={(data) => {
            let totalCount = 0;
            data.forEach(({ count }) => {
              totalCount += count;
            });

            return (
              <Table.Summary>
                <Table.Summary.Row style={{ fontWeight: "bold" }}>
                  <Table.Summary.Cell index={0} colSpan={2}>
                    รวมทั้งหมด
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align={"right"}>
                    {totalCount}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      )}
    </PageContainer>
  );
}

export default SearchCustomerLog;
